import {
  CommonModule,
  DatePipe,
  NgIf,
  registerLocaleData,
} from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  LOCALE_ID,
  OnInit,
  Output,
} from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import {
  WeatherCategory,
  weatherDayCodeMapping,
  weatherNightCodeMapping,
} from '@reenstudio/app/core/enum/wmo-codes';
import { Weather } from '@reenstudio/app/core/models/weather/weather-category';
import { WeatherService } from '@reenstudio/app/core/services/api/weather.service';
import { TablerIconsModule } from 'angular-tabler-icons';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { BehaviorSubject, Subscription } from 'rxjs';
import localeIt from '@angular/common/locales/it';
import { HttpClient } from '@angular/common/http';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { StorageService } from '@reenstudio/app/core/services/storage.service';
import { NGXLogger } from 'ngx-logger';
import { WeatherModel } from '@reenstudio/app/core/models/api/weather/weather-api-model';
import { EventService } from '@reenstudio/app/core/services/event-service';

export function HttpLoaderFactory(http: HttpClient): any {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

registerLocaleData(localeIt);

@Component({
  selector: 'app-weather',
  templateUrl: './weather.component.html',
  styleUrls: ['./weather.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    NgIf,
    MatIconModule,
    MatCardModule,
    NgxSkeletonLoaderModule,
    TablerIconsModule,
    DatePipe,
  ],
  providers: [
    { provide: LOCALE_ID, useValue: 'it' },
    { provide: MAT_DATE_LOCALE, useValue: 'it-IT' },
  ],
})
export class WeatherComponent implements OnInit {
  constructor(
    private weatherService: WeatherService,
    private storageService: StorageService,
    private logger: NGXLogger
  ) {}

  @Input() type: string = '';
  @Output() cityName: EventEmitter<string> = new EventEmitter<string>();
  _cityName: string = '';

  loading = new BehaviorSubject<boolean>(false);

  //TODO rendere dinamiche le coordinate
  latitude: number = 36.9257;
  longitude: number = 14.7244;
  cer: number | null = this.storageService.getCer();
  weatherData: WeatherModel[];
  dailyForecasts: any[] = [];
  hourlyForecasts: any[] = [];

  currentTime: string | null;

  weather: Weather = {
    date: '',
    category: '',
    temperature: '',
    alert: '',
    background: '',
    text: '',
    icon: '',
    description: '',
    sentiment: '',
    sentimentMessage: '',
    sentimentColor: '',
    direct_radiation: '',
    cloud_coverage: '',
  };

  siteSubscription: Subscription;

  ngOnInit(): void {
    if (this.type === 'sideBar' || this.type === 'dashboard') {
      this.getWeather();
    } else if (this.type === 'forecast') {
      this.getForecast();
    } else if (this.type === 'hourly') {
      this.getHourly();
    }

    EventService.changeCer.subscribe(() => {
      const cer = this.storageService.getCer();
      if (cer) {
        if (this.type === 'sideBar' || this.type === 'dashboard') {
          this.getWeather();
        } else if (this.type === 'forecast') {
          this.getForecast();
        } else if (this.type === 'hourly') {
          this.getHourly();
        }
      }
    });

    this.siteSubscription = EventService.changeSite.subscribe(() => {
      if (this.type === 'sideBar' || this.type === 'dashboard') {
        this.getWeather();
      } else if (this.type === 'forecast') {
        this.getForecast();
      } else if (this.type === 'hourly') {
        this.getHourly();
      }
    });
  }

  getWeather() {
    this.loading.next(true);
    const datePipe = new DatePipe('it-IT');

    this.weatherService
      .getCity(this.latitude, this.longitude)
      .subscribe((data: any) => {
        this.cityName.emit(data.results[0].address_components[2].long_name);
        this._cityName = data.results[0].address_components[2].long_name;
      });

    const currentDate = new Date();
    currentDate.setMinutes(0);
    currentDate.setSeconds(0);
    this.currentTime = datePipe.transform(currentDate, 'yyyy-MM-dd HH:mm:ss');

    const params = {
      cer: this.cer ? this.cer : 0,
      from: this.currentTime ? this.currentTime : '',
      to: this.currentTime ? this.currentTime : '',
    };
    this.weatherService.getWeather(params).subscribe((data) => {
      this.logger.debug('Weather received data', data);
      if (data.length) {
        const weatherData: WeatherModel[] = data;
        const weatherCode = weatherData[0].weather_code;
        let category: WeatherCategory;
        let icon: string;
        let description: string;

        if (
          weatherDayCodeMapping[weatherCode] ||
          weatherNightCodeMapping[weatherCode]
        ) {
          category = weatherDayCodeMapping[weatherCode].category;
          icon = weatherDayCodeMapping[weatherCode].icon;
          description = weatherDayCodeMapping[weatherCode].description;

          // Estrazione irradianza
          const currentDate = new Date();
          currentDate.setMinutes(0);

          const targetDate = datePipe.transform(
            currentDate,
            'yyyy-MM-ddTHH:mm'
          );

          const directRadiationValue = weatherData[0].direct_radiation;

          // Settup del messaggio di resoconto
          //TODO considerare anche l'irradianza per la determinazione del messaggio
          const cloudCover = weatherData[0].cloud_cover;
          let sentiment;
          let sentimentMessage;
          let sentimentColor;

          if (cloudCover <= 20) {
            sentiment = 'mood-smile';
            sentimentMessage = 'Produzione ottimale';
            sentimentColor = 'text-success';
          } else if (cloudCover > 20 && cloudCover <= 50) {
            sentiment = 'mood-empty';
            sentimentMessage = 'Produzione media';
            sentimentColor = 'text-warning';
          } else if (cloudCover > 50 && directRadiationValue < 100) {
            sentiment = 'mood-sad';
            sentimentMessage = 'Produzione bassa';
            sentimentColor = 'text-error';
          } else {
            sentiment = 'mood-smile';
            sentimentMessage = 'Produzione ottimale';
            sentimentColor = 'text-success';
          }

          if (directRadiationValue >= 200) {
            sentiment = 'mood-smile';
            sentimentMessage = 'Produzione ottimale';
            sentimentColor = 'text-success';
          } else if (
            directRadiationValue >= 100 &&
            directRadiationValue < 200
          ) {
            sentiment = 'mood-empty';
            sentimentMessage = 'Produzione media';
            sentimentColor = 'text-warning';
          } else if (directRadiationValue < 100) {
            sentiment = 'mood-sad';
            sentimentMessage = 'Produzione bassa';
            sentimentColor = 'text-error';
          }

          this.weather = {
            date: weatherData[0].day,
            category: WeatherCategory[category],
            icon: icon,
            temperature: weatherData[0].temperature,
            alert: 'Qualcosa non va',
            background: '#CCC',
            text: '#000',
            description: description,
            sentiment: sentiment,
            sentimentMessage: sentimentMessage,
            sentimentColor: sentimentColor,
            direct_radiation: directRadiationValue.toString(),
            cloud_coverage: cloudCover.toString(),
          };
        } else {
          console.log('Codice WMO non riconosciuto.');
        }
      }
      this.loading.next(false);
    });
  }

  getForecast() {
    this.loading.next(true);
    this.dailyForecasts = [];
    const datePipe = new DatePipe('it-IT');

    this.weatherService
      .getCity(this.latitude, this.longitude)
      .subscribe((data: any) => {
        this.cityName.emit(data.results[0].address_components[2].long_name);
        this._cityName = data.results[0].address_components[2].long_name;
      });

    this.weatherService
      .getForecast(this.latitude, this.longitude)
      .subscribe((data) => {
        if (data) {
          const weatherData: any = data;

          const days = weatherData.daily.time;
          const weatherCodes = weatherData.daily.weather_code;
          const temperatureMax = weatherData.daily.temperature_2m_max;
          const temperatureMin = weatherData.daily.temperature_2m_min;
          const precipitationProbability =
            weatherData.daily.precipitation_probability_max;

          for (let i = 0; i < days.length; i++) {
            if (weatherDayCodeMapping[weatherCodes[i]]) {
              const { icon, description } =
                weatherDayCodeMapping[weatherCodes[i]];

              const dailyForecast = {
                day: days[i],
                weatherCode: weatherCodes[i],
                maxTemperature: temperatureMax[i],
                minTemperature: temperatureMin[i],
                precipitation: precipitationProbability[i],
                icon: icon,
                description: description,
              };

              this.dailyForecasts.push(dailyForecast);
            } else {
              console.log('Codice WMO non riconosciuto.');
            }
          }
        }
        this.loading.next(false);
      });
  }

  getHourly() {
    this.loading.next(true);
    this.hourlyForecasts = [];

    this.weatherService
      .getCity(this.latitude, this.longitude)
      .subscribe((data: any) => {
        this.cityName.emit(data.results[0].address_components[2].long_name);
        this._cityName = data.results[0].address_components[2].long_name;
      });

    const datePipe = new DatePipe('it-IT');

    let toDate: string | null;
    let fromDate: string | null;

    const from = new Date();
    from.setHours(7);
    from.setMinutes(0);
    from.setSeconds(0);
    fromDate = datePipe.transform(from, 'yyyy-MM-dd HH:mm:ss');

    const to = new Date();
    to.setHours(19);
    to.setMinutes(0);
    to.setSeconds(0);
    toDate = datePipe.transform(to, 'yyyy-MM-dd HH:mm:ss');

    const params = {
      cer: this.cer ? this.cer : 0,
      from: fromDate ? fromDate : '',
      to: toDate ? toDate : '',
    };

    this.logger.debug('Weather forecast orario giornaliero params', params);

    this.weatherService.getHourly(params).subscribe((data) => {
      this.logger.debug(
        'Weather forecast orario giornaliero received data',
        data
      );
      if (data.length) {
        const weatherData: WeatherModel[] = data;

        weatherData.forEach((item) => {
          const orarioCorrente = item.hour;
          const ore = orarioCorrente;

          // Filtra gli orari compresi tra le 07:00 e le 19:00
          if (ore >= 7 && ore <= 19) {
            if (weatherDayCodeMapping[item.weather_code]) {
              const { icon, description } =
                weatherDayCodeMapping[item.weather_code];

              const hourlyForecast = {
                time: item.hour,
                weatherCode: item.weather_code,
                temperature: item.temperature,
                icon: icon,
                description: description,
              };

              this.hourlyForecasts.push(hourlyForecast);
            } else {
              console.log('Codice WMO non riconosciuto.');
            }
          }
        });
        this.logger.debug(
          'Weather forecast orario giornaliero managed data',
          this.hourlyForecasts
        );
      }
      this.loading.next(false);
    });
  }

  ngOnDestroy() {
    this.siteSubscription.unsubscribe;
  }
}
